import { useNavigate } from 'react-router-dom';
import { haptic } from '../../lib/haptic';

type Props = {
  onMenuOpen: () => void;
  onBellClick?: () => void;
  unread?: number;
  /** When set, the left button becomes a back arrow. A string is a route path;
   *  -1 means `navigate(-1)`. Replaces the hamburger menu for that route. */
  backTo?: string | -1;
  /** Centered title rendered between the two action chips. Omit to leave the
   *  center empty (the page body owns its own title). */
  title?: string;
  /** When true, the bell button is hidden entirely. Used on focused screens
   *  like Trip Detail where notifications would distract from the task. */
  hideBell?: boolean;
};

export function AppHeader({
  onMenuOpen,
  onBellClick,
  unread = 0,
  backTo,
  title,
  hideBell = false,
}: Props) {
  const navigate = useNavigate();
  const isBack = backTo !== undefined;

  const handleLeftClick = () => {
    haptic.tap();
    if (isBack) {
      if (backTo === -1) navigate(-1);
      else navigate(backTo as string);
      return;
    }
    onMenuOpen();
  };

  return (
    <header
      className="flex items-center justify-between px-4 sticky top-0 z-10"
      style={{
        height: '64px',
        // Opaque on every screen (incl. title-less home) so scrolling content
        // never shows through the bar — stakeholder flagged page titles/content
        // "scrolling above the blurry menu". The page title stays pinned here.
        background: 'var(--color-bg-page)',
      }}
    >
      <button
        type="button"
        onClick={handleLeftClick}
        aria-label={isBack ? 'Back' : 'Open menu'}
        className="flex items-center justify-center"
        style={{
          width: '44px',
          height: '44px',
          borderRadius: '12px',
          border: '1px solid var(--color-border)',
          background: 'var(--color-surface)',
        }}
      >
        {isBack ? (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
            <polyline
              points="15 18 9 12 15 6"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        ) : (
          <svg width="20" height="20" viewBox="0 0 22 22" fill="none" aria-hidden>
            <path d="M3 6h16M3 11h16M3 16h16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        )}
      </button>

      {/* Center slot — optional title for focused screens (Trip Detail).
          Most pages leave this empty and use a body headline instead. */}
      {title ? (
        <div
          className="text-[15px] font-semibold text-[var(--color-text)] tracking-tight truncate px-3"
          style={{ maxWidth: '60%' }}
        >
          {title}
        </div>
      ) : (
        <div aria-hidden />
      )}

      {hideBell ? (
        // Keep the right slot reserved so the title stays optically centered.
        <div aria-hidden style={{ width: '44px', height: '44px' }} />
      ) : (
        <button
          type="button"
          aria-label="Notifications"
          onClick={() => { haptic.tap(); onBellClick?.(); }}
          className="flex items-center justify-center relative"
          style={{
            width: '44px',
            height: '44px',
            borderRadius: '12px',
            border: '1px solid var(--color-border)',
            background: 'var(--color-surface)',
          }}
        >
          <svg width="20" height="20" viewBox="0 0 22 22" fill="none" aria-hidden>
            <path d="M5 9a6 6 0 0 1 12 0v3l1.5 2.5H3.5L5 12V9Z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
            <path d="M9 17a2 2 0 0 0 4 0" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
          </svg>
          {unread > 0 ? (
            <span
              aria-hidden
              style={{
                position: 'absolute',
                top: '-4px',
                right: '-4px',
                minWidth: '18px',
                height: '18px',
                padding: '0 4px',
                borderRadius: '9999px',
                background: '#DC2626',
                color: '#FFFFFF',
                fontSize: '10px',
                fontWeight: 700,
                lineHeight: '14px',
                textAlign: 'center',
                border: '2px solid var(--color-bg-page)',
                boxSizing: 'content-box',
              }}
            >
              {unread > 9 ? '9+' : unread}
            </span>
          ) : null}
        </button>
      )}
    </header>
  );
}
